import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Fade,
    Collapse,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import * as XLSX from 'exceljs';

import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUILoadingLinear, SCUIButton } from '../../atomic/atoms';
import { PageUrl } from '../../types/enums';
import { BinNumberQuery } from '../../services/query';
import { BinNumberCommand } from '../../services/command';
import { saveCreditBinNumbers, saveDebitBinNumbers } from './helper/binNumberSave.helper';

type CardType = 'debit' | 'credit' | null;

export default function BinNumberManagement() {
    const { t } = useTranslation();
    const [selectedCardType, setSelectedCardType] = useState<CardType>(null);

    // Debit Card States
    const [bankList, setBankList] = useState<any[]>([]);
    const [selectedBankId, setSelectedBankId] = useState<string>('');
    const [isBankLoading, setIsBankLoading] = useState(false);
    const [showDebitList, setShowDebitList] = useState(false);

    // Debit List States
    const [debitCardList, setDebitCardList] = useState<any[]>([]);
    const [isDebitListLoading, setIsDebitListLoading] = useState(false);
    const [debitListPagination, setDebitListPagination] = useState({
        page: 1,
        pageSize: 100,
        totalCount: 0,
    });

    // Credit Card States
    const [creditCardTypeList, setCreditCardTypeList] = useState<any[]>([]);
    const [selectedCreditCardTypeId, setSelectedCreditCardTypeId] = useState<string>('');
    const [isCreditCardTypeLoading, setIsCreditCardTypeLoading] = useState(false);
    const [showCreditList, setShowCreditList] = useState(false);

    // Credit List States
    const [creditCardList, setCreditCardList] = useState<any[]>([]);
    const [isCreditListLoading, setIsCreditListLoading] = useState(false);
    const [creditListPagination, setCreditListPagination] = useState({
        page: 1,
        pageSize: 100,
        totalCount: 0,
    });

    // Add New Bin Modal States
    const [isAddDebitBinModalOpen, setIsAddDebitBinModalOpen] = useState(false);
    const [isAddCreditBinModalOpen, setIsAddCreditBinModalOpen] = useState(false);

    // Bulk Excel Upload Modal States (Debit)
    const [isBulkExcelModalOpen, setIsBulkExcelModalOpen] = useState(false);
    const [selectedExcelFile, setSelectedExcelFile] = useState<File | null>(null);
    const [isExcelProcessing, setIsExcelProcessing] = useState(false);

    // Bulk Excel Upload Modal States (Credit)
    const [isBulkCreditExcelModalOpen, setIsBulkCreditExcelModalOpen] = useState(false);
    const [selectedCreditExcelFile, setSelectedCreditExcelFile] = useState<File | null>(null);
    const [isCreditExcelProcessing, setIsCreditExcelProcessing] = useState(false);

    // Debit Bin Form States
    const [debitBinNumbers, setDebitBinNumbers] = useState('');
    const [isDebitSaving, setIsDebitSaving] = useState(false);

    // Credit Bin Form States
    const [creditBinNumbers, setCreditBinNumbers] = useState('');
    const [isCreditSaving, setIsCreditSaving] = useState(false);

    // Handle Excel File Upload and Parse
    const handleExcelFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setSelectedExcelFile(file);
        setIsExcelProcessing(true);

        try {
            const workbook = new XLSX.Workbook();
            const arrayBuffer = await file.arrayBuffer();
            await workbook.xlsx.load(arrayBuffer);

            const worksheet = workbook.worksheets[0];
            const blackListedCreditCards: any[] = [];

            worksheet.eachRow((row, rowNumber) => {
                if (rowNumber === 1) return;

                const binNumbers = row.getCell(1).value;
                const bankId = row.getCell(2).value;

                if (!binNumbers || !bankId) return;

                blackListedCreditCards.push({
                    binNumbers: binNumbers?.toString().trim(),
                    bankId: Number(bankId),
                });
            });

            const requestData = {
                blackListedCreditCards: blackListedCreditCards,
                creditCardBlackListReasonId: 1,
            };

            const response = await BinNumberCommand.CreateBlackListedCreditCardsBulk(requestData);

            if (response?.statuscode && response.statuscode !== 200) {
                Swal.fire({
                    icon: 'error',
                    title: 'Hata!',
                    text: response.message || 'Toplu bin ekleme işlemi başarısız oldu.',
                    showConfirmButton: true,
                });
                setIsExcelProcessing(false);
                return;
            }

            Swal.fire({
                icon: 'success',
                title: 'Başarılı!',
                text: `${blackListedCreditCards.length} adet bin numarası başarıyla eklendi.`,
                showConfirmButton: true,
            });

            if (selectedBankId) {
                getDebitCardList(selectedBankId);
            }

            setIsBulkExcelModalOpen(false);
            setSelectedExcelFile(null);
        } catch (error) {
            console.error('Excel okuma hatası:', error);
            Swal.fire({
                icon: 'error',
                title: 'Excel Okuma Hatası',
                text: 'Excel dosyası okunamadı veya API isteği başarısız oldu. Lütfen dosya formatını kontrol edin.',
                showConfirmButton: true,
            });
        } finally {
            setIsExcelProcessing(false);
        }
    };

    // Handle Credit Card Excel File Upload and Parse
    const handleCreditExcelFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setSelectedCreditExcelFile(file);
        setIsCreditExcelProcessing(true);

        try {
            const workbook = new XLSX.Workbook();
            const arrayBuffer = await file.arrayBuffer();
            await workbook.xlsx.load(arrayBuffer);

            const worksheet = workbook.worksheets[0];
            const createCreditCardTypeBinNumbers: any[] = [];

            worksheet.eachRow((row, rowNumber) => {
                if (rowNumber === 1) return;

                const binNumbers = row.getCell(1).value;
                const creditCardTypeId = row.getCell(2).value;

                if (!binNumbers || !creditCardTypeId) return;

                createCreditCardTypeBinNumbers.push({
                    binNumbers: binNumbers?.toString().trim(),
                    creditCardTypeId: Number(creditCardTypeId),
                });
            });

            const requestData = {
                createCreditCardTypeBinNumbers: createCreditCardTypeBinNumbers,
            };

            const response = await BinNumberCommand.CreateCreditCardTypeBinNumbersBulk(requestData);

            if (response?.statuscode && response.statuscode !== 200) {
                Swal.fire({
                    icon: 'error',
                    title: 'Hata!',
                    text: response.message || 'Toplu bin ekleme işlemi başarısız oldu.',
                    showConfirmButton: true,
                });
                setIsCreditExcelProcessing(false);
                return;
            }

            Swal.fire({
                icon: 'success',
                title: 'Başarılı!',
                text: `${createCreditCardTypeBinNumbers.length} adet bin numarası başarıyla eklendi.`,
                showConfirmButton: true,
            });

            if (selectedCreditCardTypeId) {
                getCreditCardList(selectedCreditCardTypeId);
            }

            setIsBulkCreditExcelModalOpen(false);
            setSelectedCreditExcelFile(null);
        } catch (error) {
            console.error('Credit Card Excel okuma hatası:', error);
            Swal.fire({
                icon: 'error',
                title: 'Excel Okuma Hatası',
                text: 'Excel dosyası okunamadı veya API isteği başarısız oldu. Lütfen dosya formatını kontrol edin.',
                showConfirmButton: true,
            });
        } finally {
            setIsCreditExcelProcessing(false);
        }
    };

    const handleDebitBinSave = async () => {
        await saveDebitBinNumbers({
            debitBinNumbers,
            selectedBankId,
            t,
            setIsDebitSaving,
            setDebitBinNumbers,
            setIsAddDebitBinModalOpen,
        });
    };

    const handleCreditBinSave = async () => {
        await saveCreditBinNumbers({
            creditBinNumbers,
            selectedCreditCardTypeId,
            t,
            setIsCreditSaving,
            setCreditBinNumbers,
            setIsAddCreditBinModalOpen,
            refreshCreditCardList: getCreditCardList,
        });
    };

    const handleCardTypeSelect = (cardType: CardType) => {
        setSelectedCardType(cardType);

        setSelectedBankId('');
        setShowDebitList(false);
        setSelectedCreditCardTypeId('');
        setShowCreditList(false);

        if (cardType === 'debit') {
            getBankList();
        }

        if (cardType === 'credit') {
            getCreditCardTypeList();
        }
    };

    const handleCreditCardTypeSelect = (creditCardTypeId: string) => {
        setSelectedCreditCardTypeId(creditCardTypeId);

        if (creditCardTypeId) {
            setShowCreditList(true);
            getCreditCardList(creditCardTypeId);
        }
    };

    const getCreditCardList = async (creditCardTypeId?: string) => {
        setIsCreditListLoading(true);
        try {
            const params = {
                creditCardTypeId: creditCardTypeId || selectedCreditCardTypeId,
                page: creditListPagination.page,
                pageSize: creditListPagination.pageSize,
            };

            const response = await BinNumberQuery.GetCreditCardTypeBinNumbers(params);

            if (response && response.model && Array.isArray(response.model)) {
                setCreditCardList(response.model);
                setCreditListPagination((prev) => ({
                    ...prev,
                    totalCount: response.totalCount || response.model.length,
                }));
            } else {
                setCreditCardList([]);
            }
        } catch {
            setCreditCardList([]);
        } finally {
            setIsCreditListLoading(false);
        }
    };

    const handleBankSelect = (bankId: string) => {
        setSelectedBankId(bankId);
        if (bankId) {
            setShowDebitList(true);
            getDebitCardList(bankId);
        }
    };

    const getDebitCardList = async (bankId?: string) => {
        setIsDebitListLoading(true);
        try {
            const selectedBank = bankList.find((bank) => bank.bankId.toString() === (bankId || selectedBankId));
            const binNumber = selectedBank?.bankCode || '';

            const params = {
                binNumber: binNumber,
                page: debitListPagination.page,
                pageSize: debitListPagination.pageSize,
            };

            const response = await BinNumberQuery.GetBlackListCreditCards(params);

            if (response && response.model && Array.isArray(response.model)) {
                setDebitCardList(response.model);
                setDebitListPagination((prev) => ({
                    ...prev,
                    totalCount: response.totalCount || response.model.length,
                }));
            } else {
                setDebitCardList([]);
            }
        } catch {
            setDebitCardList([]);
        } finally {
            setIsDebitListLoading(false);
        }
    };

    const getBankList = async () => {
        setIsBankLoading(true);
        try {
            const response = await BinNumberQuery.GetBanks(1);

            if (response && response.model && Array.isArray(response.model)) {
                setBankList(response.model);
            } else {
                setBankList([]);
            }
        } catch {
            setBankList([]);
        } finally {
            setIsBankLoading(false);
        }
    };

    const getCreditCardTypeList = async () => {
        setIsCreditCardTypeLoading(true);
        try {
            const response = await BinNumberQuery.GetCreditCardTypes();

            if (response && response.model && Array.isArray(response.model)) {
                setCreditCardTypeList(response.model);
            } else {
                setCreditCardTypeList([]);
            }
        } catch {
            setCreditCardTypeList([]);
        } finally {
            setIsCreditCardTypeLoading(false);
        }
    };

    // Debit Card Delete Handler
    const handleDeleteDebitCard = async (item: any) => {
        const result = await Swal.fire({
            title: '⚠️ ' + t('binNumberManagement.messages.deleteConfirmTitle'),
            html: `
                <div style="text-align: left; padding: 10px;">
                    <p style="margin-bottom: 12px;"><strong>${t('binNumberManagement.messages.deleteConfirmMessage')}</strong></p>
                    <div style="background-color: #f8f9fa; padding: 12px; border-radius: 8px; border-left: 4px solid #1976d2;">
                        <p style="margin: 4px 0;"><strong>🆔 ${t('binNumberManagement.table.id')}:</strong> ${item.blackListedCreditCardId || item.blacklistedCreditCardId || 'N/A'}</p>
                        <p style="margin: 4px 0;"><strong>💳 ${t('binNumberManagement.table.cardNumber')}:</strong> ${item.creditCardNumber || 'N/A'}</p>
                        <p style="margin: 4px 0;"><strong>🏦 ${t('binNumberManagement.table.bankName')}:</strong> ${item.bankName || 'N/A'}</p>
                    </div>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#757575',
            confirmButtonText: '✅ ' + t('binNumberManagement.buttons.yesDelete'),
            cancelButtonText: '❌ ' + t('binNumberManagement.buttons.cancel'),
            reverseButtons: true,
            focusCancel: true,
            customClass: {
                popup: 'swal-wide',
            },
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            Swal.fire({
                title: '⏳ ' + t('binNumberManagement.messages.deleting'),
                html: t('binNumberManagement.messages.pleaseWait'),
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            const itemId = item.creditCardNumber;
            const response = await BinNumberCommand.DeleteBlackListedCreditCards(itemId);

            if (response?.isSuccess === false || response?.issuccess === false) {
                let errorMessage = response?.error || t('binNumberManagement.messages.deleteFailed');

                Swal.fire({
                    icon: 'error',
                    title: t('binNumberManagement.messages.operationFailedTitle'),
                    text: errorMessage,
                    confirmButtonText: t('binNumberManagement.buttons.ok'),
                    confirmButtonColor: '#d32f2f',
                });
                return;
            }

            Swal.fire({
                icon: 'success',
                title: '✅ ' + t('binNumberManagement.messages.success'),
                text: t('binNumberManagement.messages.recordDeletedSuccess'),
                showConfirmButton: false,
                timer: 2000,
            });

            getDebitCardList(selectedBankId);
        } catch (error) {
            console.error('Delete Error:', error);

            let errorMessage = t('binNumberManagement.messages.deleteError');

            if ((error as any)?.data?.message) {
                errorMessage = (error as any).data.message;
            } else if ((error as any)?.message) {
                errorMessage = (error as any).message;
            }

            Swal.fire({
                icon: 'error',
                title: '❌ ' + t('binNumberManagement.messages.errorTitle'),
                text: errorMessage,
                confirmButtonText: t('binNumberManagement.buttons.ok'),
                confirmButtonColor: '#d32f2f',
            });
        }
    };

    // Credit Card Delete Handler
    const handleDeleteCreditCard = async (item: any) => {
        const result = await Swal.fire({
            title: '⚠️ ' + t('binNumberManagement.messages.deleteConfirmTitle'),
            html: `
                <div style="text-align: left; padding: 10px;">
                    <p style="margin-bottom: 12px;"><strong>${t('binNumberManagement.messages.deleteConfirmMessage')}</strong></p>
                    <div style="background-color: #f8f9fa; padding: 12px; border-radius: 8px; border-left: 4px solid #2e7d32;">
                        <p style="margin: 4px 0;"><strong>🆔 ${t('binNumberManagement.table.id')}:</strong> ${item.creditCardTypeBinNumberId || 'N/A'}</p>
                        <p style="margin: 4px 0;"><strong>💳 ${t('binNumberManagement.table.binNumber')}:</strong> ${item.binNumber || 'N/A'}</p>
                        <p style="margin: 4px 0;"><strong>🎫 ${t('binNumberManagement.table.creditCardType')}:</strong> ${item.creditCardTypeId || 'N/A'}</p>
                    </div>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#757575',
            confirmButtonText: '✅ ' + t('binNumberManagement.buttons.yesDelete'),
            cancelButtonText: '❌ ' + t('binNumberManagement.buttons.cancel'),
            reverseButtons: true,
            focusCancel: true,
            customClass: {
                popup: 'swal-wide',
            },
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            Swal.fire({
                title: '⏳ ' + t('binNumberManagement.messages.deleting'),
                html: t('binNumberManagement.messages.pleaseWait'),
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            const response = await BinNumberCommand.DeleteCreditCardTypeBinNumbers(item.binNumber);

            if (response?.isSuccess === false || response?.issuccess === false) {
                let errorMessage = response?.error || t('binNumberManagement.messages.deleteFailed');

                Swal.fire({
                    icon: 'error',
                    title: '❌ ' + t('binNumberManagement.messages.operationFailedTitle'),
                    text: errorMessage,
                    confirmButtonText: t('binNumberManagement.buttons.ok'),
                    confirmButtonColor: '#d32f2f',
                });
                return;
            }

            Swal.fire({
                icon: 'success',
                title: '✅ ' + t('binNumberManagement.messages.success'),
                text: t('binNumberManagement.messages.recordDeletedSuccess'),
                showConfirmButton: false,
                timer: 2000,
            });

            getCreditCardList(selectedCreditCardTypeId);
        } catch (error) {
            console.error('Delete Error:', error);

            let errorMessage = t('binNumberManagement.messages.deleteError');

            if ((error as any)?.data?.message) {
                errorMessage = (error as any).data.message;
            } else if ((error as any)?.message) {
                errorMessage = (error as any).message;
            }

            Swal.fire({
                icon: 'error',
                title: '❌ ' + t('binNumberManagement.messages.error'),
                text: errorMessage,
                confirmButtonText: t('binNumberManagement.buttons.ok'),
                confirmButtonColor: '#d32f2f',
            });
        }
    };

    return (
        <SCUIContent>
            <Grid className="bin-number-management" container spacing={3}>
                {/* Breadcrumb */}
                <Grid
                    className="bin-number-management-breadcrumb"
                    size={{
                        xs: 12,
                    }}
                >
                    <SCUIBreadCrumb
                        title={t('binNumberManagement.title')}
                        componentLink={PageUrl.binNumberManagement.url}
                    />
                </Grid>

                {/* Main Content */}
                <Grid
                    className="bin-number-management-main"
                    size={{
                        xs: 12,
                    }}
                >
                    <Paper
                        className="bin-number-management-paper"
                        sx={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '32px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            border: '1px solid #f0f0f0',
                        }}
                    >
                        {/* Header */}
                        <Box className="bin-number-management-hero" sx={{ textAlign: 'center', marginBottom: '40px' }}>
                            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                                {t('binNumberManagement.title')}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '16px' }}>
                                {t('binNumberManagement.description')}
                            </Typography>
                        </Box>

                        {/* Card Type Selection Buttons */}
                        <Box
                            className="bin-number-management-card-type-buttons"
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '24px',
                                marginBottom: '40px',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Box className="bin-number-management-tab-debit-wrap" sx={{ position: 'relative' }}>
                                <SCUIButton
                                    className="bin-number-management-tab-debit"
                                    text={t('binNumberManagement.tabs.debitCard')}
                                    type={selectedCardType === 'debit' ? 'primary' : 'secondary'}
                                    sizeType="fixed"
                                    size="l"
                                    onClick={() => handleCardTypeSelect('debit')}
                                    style={{
                                        minWidth: '180px',
                                        height: '56px',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        borderRadius: '12px',
                                        boxShadow:
                                            selectedCardType === 'debit'
                                                ? '0 4px 12px rgba(25, 118, 210, 0.3)'
                                                : '0 2px 8px rgba(0,0,0,0.1)',
                                        transform: selectedCardType === 'debit' ? 'translateY(-2px)' : 'none',
                                        transition: 'all 0.3s ease',
                                    }}
                                />
                                {selectedCardType === 'debit' && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: '-8px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: '40px',
                                            height: '4px',
                                            backgroundColor: '#1976d2',
                                            borderRadius: '2px',
                                        }}
                                    />
                                )}
                            </Box>

                            <Box className="bin-number-management-tab-credit-wrap" sx={{ position: 'relative' }}>
                                <SCUIButton
                                    className="bin-number-management-tab-credit"
                                    text={t('binNumberManagement.tabs.creditCard')}
                                    type={selectedCardType === 'credit' ? 'primary' : 'secondary'}
                                    sizeType="fixed"
                                    size="l"
                                    onClick={() => handleCardTypeSelect('credit')}
                                    style={{
                                        minWidth: '180px',
                                        height: '56px',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        borderRadius: '12px',
                                        boxShadow:
                                            selectedCardType === 'credit'
                                                ? '0 4px 12px rgba(25, 118, 210, 0.3)'
                                                : '0 2px 8px rgba(0,0,0,0.1)',
                                        transform: selectedCardType === 'credit' ? 'translateY(-2px)' : 'none',
                                        transition: 'all 0.3s ease',
                                    }}
                                />
                                {selectedCardType === 'credit' && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: '-8px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: '40px',
                                            height: '4px',
                                            backgroundColor: '#1976d2',
                                            borderRadius: '2px',
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* Filter Areas */}
                        <Collapse in={selectedCardType !== null} timeout={500}>
                            <Fade in={selectedCardType !== null} timeout={700}>
                                <Box className="bin-number-management-filter-panel">
                                    {selectedCardType === 'debit' && !showDebitList && (
                                        <Paper
                                            className="bin-number-management-debit-filters"
                                            sx={{
                                                padding: '32px',
                                                backgroundColor: '#f8f9fa',
                                                borderRadius: '16px',
                                                border: '2px solid #e3f2fd',
                                                marginTop: '8px',
                                                boxShadow: '0 8px 24px rgba(25, 118, 210, 0.1)',
                                            }}
                                        >
                                            <Box sx={{ textAlign: 'center', marginBottom: '32px' }}>
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        color: '#1976d2',
                                                        fontWeight: 600,
                                                        marginBottom: '8px',
                                                    }}
                                                >
                                                    🔵 {t('binNumberManagement.filters.debitCardFilters')}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ fontSize: '14px' }}
                                                >
                                                    {t('binNumberManagement.filters.selectBankDescription')}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    position: 'relative',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: '400px',
                                                        position: 'relative',
                                                    }}
                                                >
                                                    {/* Decorative elements */}
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '-10px',
                                                            left: '-10px',
                                                            width: '20px',
                                                            height: '20px',
                                                            backgroundColor: '#bbdefb',
                                                            borderRadius: '50%',
                                                            opacity: 0.6,
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: '-10px',
                                                            right: '-10px',
                                                            width: '16px',
                                                            height: '16px',
                                                            backgroundColor: '#e3f2fd',
                                                            borderRadius: '50%',
                                                            opacity: 0.8,
                                                        }}
                                                    />

                                                    <FormControl fullWidth variant="outlined">
                                                        <InputLabel
                                                            id="bank-select-label"
                                                            sx={{
                                                                color: '#1976d2',
                                                                fontWeight: 500,
                                                                '&.Mui-focused': {
                                                                    color: '#1976d2',
                                                                },
                                                            }}
                                                        >
                                                            🏦 {t('binNumberManagement.filters.selectBank')}
                                                        </InputLabel>
                                                        <Select
                                                            labelId="bank-select-label"
                                                            value={selectedBankId}
                                                            onChange={(e) => handleBankSelect(e.target.value)}
                                                            label={`🏦 ${t('binNumberManagement.filters.selectBank')}`}
                                                            disabled={isBankLoading}
                                                            sx={{
                                                                backgroundColor: 'white',
                                                                borderRadius: '12px',
                                                                fontSize: '16px',
                                                                fontWeight: 500,
                                                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.1)',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#e3f2fd',
                                                                    borderWidth: '2px',
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#1976d2',
                                                                    borderWidth: '2px',
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#1976d2',
                                                                    borderWidth: '2px',
                                                                },
                                                                '& .MuiSelect-select': {
                                                                    padding: '16px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                },
                                                                '& .MuiInputLabel-root': {
                                                                    transform: 'translate(14px, 16px) scale(1)',
                                                                },
                                                                '& .MuiInputLabel-shrink': {
                                                                    transform: 'translate(14px, -9px) scale(0.75)',
                                                                },
                                                            }}
                                                            MenuProps={{
                                                                PaperProps: {
                                                                    sx: {
                                                                        borderRadius: '12px',
                                                                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                                                        border: '1px solid #e3f2fd',
                                                                        '& .MuiMenuItem-root': {
                                                                            padding: '12px 16px',
                                                                            fontSize: '16px',
                                                                            '&:hover': {
                                                                                backgroundColor: '#e3f2fd',
                                                                            },
                                                                            '&.Mui-selected': {
                                                                                backgroundColor: '#bbdefb',
                                                                                '&:hover': {
                                                                                    backgroundColor: '#90caf9',
                                                                                },
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            {isBankLoading ? (
                                                                <MenuItem disabled>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '8px',
                                                                        }}
                                                                    >
                                                                        ⏳{' '}
                                                                        <em>
                                                                            {t('binNumberManagement.loading.loading')}
                                                                        </em>
                                                                    </Box>
                                                                </MenuItem>
                                                            ) : bankList.length === 0 ? (
                                                                <MenuItem disabled>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '8px',
                                                                        }}
                                                                    >
                                                                        ❌{' '}
                                                                        <em>
                                                                            {t(
                                                                                'binNumberManagement.loading.bankNotFound'
                                                                            )}
                                                                        </em>
                                                                    </Box>
                                                                </MenuItem>
                                                            ) : (
                                                                bankList.map((bank) => (
                                                                    <MenuItem key={bank.bankId} value={bank.bankId}>
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '8px',
                                                                            }}
                                                                        >
                                                                            🏛️ {bank.bankName}
                                                                        </Box>
                                                                    </MenuItem>
                                                                ))
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            </Box>

                                            {/* Bottom decorative text */}
                                            <Box sx={{ textAlign: 'center', marginTop: '24px' }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontStyle: 'italic',
                                                        opacity: 0.7,
                                                    }}
                                                >
                                                    Banka seçimi sonrasında detaylı liste görüntülenecektir
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    )}

                                    {selectedCardType === 'debit' && showDebitList && (
                                        <Paper
                                            sx={{
                                                padding: '24px',
                                                backgroundColor: '#f8f9fa',
                                                borderRadius: '12px',
                                                border: '2px solid #e3f2fd',
                                                marginTop: '8px',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginBottom: '20px',
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        color: '#1976d2',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    🔵 {t('binNumberManagement.listHeaders.debitCardList')}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: '12px' }}>
                                                    <SCUIButton
                                                        text={`🔄 ${t('binNumberManagement.buttons.backToFilter')}`}
                                                        type="secondary"
                                                        sizeType="fixed"
                                                        size="s"
                                                        onClick={() => setShowDebitList(false)}
                                                    />
                                                    <SCUIButton
                                                        text={`➕ ${t('binNumberManagement.buttons.addNewBin')}`}
                                                        type="secondary"
                                                        sizeType="fixed"
                                                        size="s"
                                                        onClick={() => setIsAddDebitBinModalOpen(true)}
                                                    />
                                                    <SCUIButton
                                                        text={`📄 ${t('binNumberManagement.buttons.bulkAddFromExcel')}`}
                                                        type="secondary"
                                                        sizeType="fixed"
                                                        size="s"
                                                        onClick={() => setIsBulkExcelModalOpen(true)}
                                                    />
                                                </Box>
                                            </Box>

                                            {/* Seçilen Banka Bilgisi */}
                                            <Box
                                                sx={{
                                                    marginBottom: '20px',
                                                    padding: '12px',
                                                    backgroundColor: '#e3f2fd',
                                                    borderRadius: '8px',
                                                    border: '1px solid #bbdefb',
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    Seçilen Banka:
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 500, color: '#1976d2' }}>
                                                    {(() => {
                                                        const selectedBank = bankList.find(
                                                            (bank) =>
                                                                bank.bankId == selectedBankId ||
                                                                bank.bankId.toString() === selectedBankId ||
                                                                bank.bankId === parseInt(selectedBankId)
                                                        );

                                                        return selectedBank?.bankName || 'Banka seçilmedi';
                                                    })()}
                                                </Typography>
                                            </Box>

                                            {/* Liste Alanı */}
                                            <Box
                                                sx={{
                                                    borderRadius: '8px',
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e3f2fd',
                                                }}
                                            >
                                                {isDebitListLoading ? (
                                                    <Box
                                                        sx={{
                                                            minHeight: '300px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexDirection: 'column',
                                                            gap: '16px',
                                                        }}
                                                    >
                                                        <Typography variant="h6" color="text.secondary">
                                                            ⏳ {t('binNumberManagement.loading.loadingData')}
                                                        </Typography>
                                                        <SCUILoadingLinear display={true} />
                                                    </Box>
                                                ) : debitCardList.length === 0 ? (
                                                    <Box
                                                        sx={{
                                                            minHeight: '300px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexDirection: 'column',
                                                            gap: '16px',
                                                        }}
                                                    >
                                                        <Typography variant="h6" color="text.secondary">
                                                            📋 Veri Bulunamadı
                                                        </Typography>
                                                        <Typography
                                                            variant="body1"
                                                            color="text.secondary"
                                                            sx={{ fontStyle: 'italic', textAlign: 'center' }}
                                                        >
                                                            Bu banka için kayıtlı debit kart verisi bulunmuyor
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box>
                                                        {/* Tablo Header */}
                                                        <Box
                                                            sx={{
                                                                padding: '16px',
                                                                backgroundColor: '#f5f5f5',
                                                                borderBottom: '1px solid #e0e0e0',
                                                                borderRadius: '8px 8px 0 0',
                                                            }}
                                                        >
                                                            <Grid container>
                                                                <Grid
                                                                    size={{
                                                                        xs: 2,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{ fontWeight: 600 }}
                                                                    >
                                                                        ID
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid
                                                                    size={{
                                                                        xs: 2,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{ fontWeight: 600 }}
                                                                    >
                                                                        Credit Card Number
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid
                                                                    size={{
                                                                        xs: 3,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{ fontWeight: 600 }}
                                                                    >
                                                                        Reason ID
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid
                                                                    size={{
                                                                        xs: 3,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{ fontWeight: 600 }}
                                                                    >
                                                                        Bank ID
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid
                                                                    size={{
                                                                        xs: 2,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{ fontWeight: 600, textAlign: 'center' }}
                                                                    >
                                                                        İşlemler
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>

                                                        {/* Tablo Content */}
                                                        <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                            {debitCardList.map((item, index) => (
                                                                <Box
                                                                    key={
                                                                        item.blackListedCreditCardId ||
                                                                        item.blacklistedCreditCardId ||
                                                                        index
                                                                    }
                                                                    sx={{
                                                                        padding: '12px 16px',
                                                                        borderBottom: '1px solid #f0f0f0',
                                                                        '&:hover': {
                                                                            backgroundColor: '#f8f9fa',
                                                                        },
                                                                        '&:last-child': {
                                                                            borderBottom: 'none',
                                                                        },
                                                                    }}
                                                                >
                                                                    <Grid container alignItems="center">
                                                                        <Grid
                                                                            size={{
                                                                                xs: 2,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{ fontWeight: 500 }}
                                                                            >
                                                                                {item.blackListedCreditCardId ||
                                                                                    item.blacklistedCreditCardId ||
                                                                                    '-'}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid
                                                                            size={{
                                                                                xs: 2,
                                                                            }}
                                                                        >
                                                                            <Typography variant="body2">
                                                                                {item.creditCardNumber || '-'}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid
                                                                            size={{
                                                                                xs: 3,
                                                                            }}
                                                                        >
                                                                            <Typography variant="body2">
                                                                                {item.creditCardBlackListReasonId ||
                                                                                    '-'}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid
                                                                            size={{
                                                                                xs: 3,
                                                                            }}
                                                                        >
                                                                            <Typography variant="body2">
                                                                                {item.bankId || '-'}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid
                                                                            sx={{ textAlign: 'center' }}
                                                                            size={{
                                                                                xs: 2,
                                                                            }}
                                                                        >
                                                                            <Tooltip title="Sil" arrow>
                                                                                <IconButton
                                                                                    onClick={() =>
                                                                                        handleDeleteDebitCard(item)
                                                                                    }
                                                                                    sx={{
                                                                                        color: '#d32f2f',
                                                                                        '&:hover': {
                                                                                            backgroundColor: '#ffebee',
                                                                                            color: '#c62828',
                                                                                        },
                                                                                        transition: 'all 0.2s ease',
                                                                                    }}
                                                                                    size="small"
                                                                                >
                                                                                    <DeleteIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Box>
                                                            ))}
                                                        </Box>

                                                        {/* Pagination Info */}
                                                        <Box
                                                            sx={{
                                                                padding: '16px',
                                                                backgroundColor: '#f8f9fa',
                                                                borderTop: '1px solid #e0e0e0',
                                                                borderRadius: '0 0 8px 8px',
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <Typography variant="body2" color="text.secondary">
                                                                Toplam {debitCardList.length} kayıt gösteriliyor
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Paper>
                                    )}

                                    {selectedCardType === 'credit' && !showCreditList && (
                                        <Paper
                                            className="bin-number-management-credit-filters"
                                            sx={{
                                                padding: '32px',
                                                backgroundColor: '#f8f9fa',
                                                borderRadius: '16px',
                                                border: '2px solid #e8f5e8',
                                                marginTop: '8px',
                                                boxShadow: '0 8px 24px rgba(46, 125, 50, 0.1)',
                                            }}
                                        >
                                            <Box sx={{ textAlign: 'center', marginBottom: '32px' }}>
                                                <Typography
                                                    variant="h5"
                                                    sx={{
                                                        color: '#2e7d32',
                                                        fontWeight: 600,
                                                        marginBottom: '8px',
                                                    }}
                                                >
                                                    💳 {t('binNumberManagement.filters.creditCardFilters')}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ fontSize: '14px' }}
                                                >
                                                    {t('binNumberManagement.filters.selectCreditCardDescription')}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    position: 'relative',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: '400px',
                                                        position: 'relative',
                                                    }}
                                                >
                                                    {/* Decorative elements */}
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '-10px',
                                                            left: '-10px',
                                                            width: '20px',
                                                            height: '20px',
                                                            backgroundColor: '#c8e6c9',
                                                            borderRadius: '50%',
                                                            opacity: 0.6,
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: '-10px',
                                                            right: '-10px',
                                                            width: '16px',
                                                            height: '16px',
                                                            backgroundColor: '#e8f5e8',
                                                            borderRadius: '50%',
                                                            opacity: 0.8,
                                                        }}
                                                    />

                                                    <FormControl fullWidth variant="outlined">
                                                        <InputLabel
                                                            id="credit-card-type-select-label"
                                                            sx={{
                                                                color: '#2e7d32',
                                                                fontWeight: 500,
                                                                '&.Mui-focused': {
                                                                    color: '#2e7d32',
                                                                },
                                                            }}
                                                        >
                                                            💳 {t('binNumberManagement.filters.selectCreditCardType')}
                                                        </InputLabel>
                                                        <Select
                                                            labelId="credit-card-type-select-label"
                                                            value={selectedCreditCardTypeId}
                                                            onChange={(e) => handleCreditCardTypeSelect(e.target.value)}
                                                            label={`💳 ${t('binNumberManagement.filters.selectCreditCardType')}`}
                                                            disabled={isCreditCardTypeLoading}
                                                            sx={{
                                                                backgroundColor: 'white',
                                                                borderRadius: '12px',
                                                                fontSize: '16px',
                                                                fontWeight: 500,
                                                                boxShadow: '0 4px 12px rgba(46, 125, 50, 0.1)',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#e8f5e8',
                                                                    borderWidth: '2px',
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#2e7d32',
                                                                    borderWidth: '2px',
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#2e7d32',
                                                                    borderWidth: '2px',
                                                                },
                                                                '& .MuiSelect-select': {
                                                                    padding: '16px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                },
                                                                '& .MuiInputLabel-root': {
                                                                    transform: 'translate(14px, 16px) scale(1)',
                                                                },
                                                                '& .MuiInputLabel-shrink': {
                                                                    transform: 'translate(14px, -9px) scale(0.75)',
                                                                },
                                                            }}
                                                            MenuProps={{
                                                                PaperProps: {
                                                                    sx: {
                                                                        borderRadius: '12px',
                                                                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                                                        border: '1px solid #e8f5e8',
                                                                        '& .MuiMenuItem-root': {
                                                                            padding: '12px 16px',
                                                                            fontSize: '16px',
                                                                            '&:hover': {
                                                                                backgroundColor: '#e8f5e8',
                                                                            },
                                                                            '&.Mui-selected': {
                                                                                backgroundColor: '#c8e6c9',
                                                                                '&:hover': {
                                                                                    backgroundColor: '#a5d6a7',
                                                                                },
                                                                            },
                                                                        },
                                                                    },
                                                                },
                                                            }}
                                                        >
                                                            {isCreditCardTypeLoading ? (
                                                                <MenuItem disabled>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '8px',
                                                                        }}
                                                                    >
                                                                        ⏳{' '}
                                                                        <em>
                                                                            {t('binNumberManagement.loading.loading')}
                                                                        </em>
                                                                    </Box>
                                                                </MenuItem>
                                                            ) : creditCardTypeList.length === 0 ? (
                                                                <MenuItem disabled>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '8px',
                                                                        }}
                                                                    >
                                                                        ❌{' '}
                                                                        <em>
                                                                            {t(
                                                                                'binNumberManagement.loading.creditCardTypeNotFound'
                                                                            )}
                                                                        </em>
                                                                    </Box>
                                                                </MenuItem>
                                                            ) : (
                                                                creditCardTypeList.map((cardType) => (
                                                                    <MenuItem
                                                                        key={cardType.creditCardTypeId}
                                                                        value={cardType.creditCardTypeId}
                                                                    >
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '8px',
                                                                            }}
                                                                        >
                                                                            💎 {cardType.creditCardTypeName}
                                                                        </Box>
                                                                    </MenuItem>
                                                                ))
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            </Box>

                                            {/* Bottom decorative text */}
                                            <Box sx={{ textAlign: 'center', marginTop: '24px' }}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontStyle: 'italic',
                                                        opacity: 0.7,
                                                    }}
                                                >
                                                    {t('binNumberManagement.messages.cardTypeSelectionInfo')}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    )}

                                    {selectedCardType === 'credit' && showCreditList && (
                                        <Paper
                                            sx={{
                                                padding: '24px',
                                                backgroundColor: '#f8f9fa',
                                                borderRadius: '12px',
                                                border: '2px solid #e8f5e8',
                                                marginTop: '8px',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginBottom: '20px',
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        color: '#2e7d32',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    💳 {t('binNumberManagement.listHeaders.creditCardList')}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: '12px' }}>
                                                    <SCUIButton
                                                        text={`🔄 ${t('binNumberManagement.buttons.backToFilter')}`}
                                                        type="secondary"
                                                        sizeType="fixed"
                                                        size="s"
                                                        onClick={() => setShowCreditList(false)}
                                                    />
                                                    <SCUIButton
                                                        text={`➕ ${t('binNumberManagement.buttons.addNewBin')}`}
                                                        type="secondary"
                                                        sizeType="fixed"
                                                        size="s"
                                                        onClick={() => setIsAddCreditBinModalOpen(true)}
                                                    />
                                                    <SCUIButton
                                                        text={`📄 ${t('binNumberManagement.buttons.bulkAddFromExcel')}`}
                                                        type="secondary"
                                                        sizeType="fixed"
                                                        size="s"
                                                        onClick={() => setIsBulkCreditExcelModalOpen(true)}
                                                    />
                                                </Box>
                                            </Box>

                                            {/* Seçilen Kredi Kart Tipi Bilgisi */}
                                            <Box
                                                sx={{
                                                    marginBottom: '20px',
                                                    padding: '12px',
                                                    backgroundColor: '#e8f5e8',
                                                    borderRadius: '8px',
                                                    border: '1px solid #c8e6c9',
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    {t('binNumberManagement.info.cardTypeSelectionInfo')}
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                                                    {(() => {
                                                        const selectedType = creditCardTypeList.find(
                                                            (type) =>
                                                                type.creditCardTypeId == selectedCreditCardTypeId ||
                                                                type.creditCardTypeId.toString() ===
                                                                    selectedCreditCardTypeId ||
                                                                type.creditCardTypeId ===
                                                                    parseInt(selectedCreditCardTypeId)
                                                        );

                                                        return selectedType?.creditCardTypeName || 'Tip seçilmedi';
                                                    })()}
                                                </Typography>
                                            </Box>

                                            {/* Liste Alanı */}
                                            <Box
                                                sx={{
                                                    borderRadius: '8px',
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e8f5e8',
                                                }}
                                            >
                                                {isCreditListLoading ? (
                                                    <Box
                                                        sx={{
                                                            minHeight: '300px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexDirection: 'column',
                                                            gap: '16px',
                                                        }}
                                                    >
                                                        <Typography variant="h6" color="text.secondary">
                                                            ⏳ {t('binNumberManagement.loading.loadingData')}
                                                        </Typography>
                                                        <SCUILoadingLinear display={true} />
                                                    </Box>
                                                ) : creditCardList.length === 0 ? (
                                                    <Box
                                                        sx={{
                                                            minHeight: '300px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexDirection: 'column',
                                                            gap: '16px',
                                                        }}
                                                    >
                                                        <Typography variant="h6" color="text.secondary">
                                                            📋 Veri Bulunamadı
                                                        </Typography>
                                                        <Typography
                                                            variant="body1"
                                                            color="text.secondary"
                                                            sx={{ fontStyle: 'italic', textAlign: 'center' }}
                                                        >
                                                            Bu kredi kart tipi için kayıtlı bin number verisi bulunmuyor
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box>
                                                        {/* Tablo Header */}
                                                        <Box
                                                            sx={{
                                                                padding: '16px',
                                                                backgroundColor: '#f5f5f5',
                                                                borderBottom: '1px solid #e0e0e0',
                                                                borderRadius: '8px 8px 0 0',
                                                            }}
                                                        >
                                                            <Grid container>
                                                                <Grid
                                                                    size={{
                                                                        xs: 3,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{ fontWeight: 600 }}
                                                                    >
                                                                        Credit Card Type Bin Number ID
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid
                                                                    size={{
                                                                        xs: 3,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{ fontWeight: 600 }}
                                                                    >
                                                                        Credit Card Type ID
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid
                                                                    size={{
                                                                        xs: 4,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{ fontWeight: 600 }}
                                                                    >
                                                                        Bin Number
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid
                                                                    size={{
                                                                        xs: 2,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        sx={{ fontWeight: 600, textAlign: 'center' }}
                                                                    >
                                                                        İşlemler
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </Box>

                                                        {/* Tablo Content */}
                                                        <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                            {creditCardList.map((item, index) => (
                                                                <Box
                                                                    key={item.creditCardTypeBinNumberId || index}
                                                                    sx={{
                                                                        padding: '12px 16px',
                                                                        borderBottom: '1px solid #f0f0f0',
                                                                        '&:hover': {
                                                                            backgroundColor: '#f8f9fa',
                                                                        },
                                                                        '&:last-child': {
                                                                            borderBottom: 'none',
                                                                        },
                                                                    }}
                                                                >
                                                                    <Grid container alignItems="center">
                                                                        <Grid
                                                                            size={{
                                                                                xs: 3,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{ fontWeight: 500 }}
                                                                            >
                                                                                {item.creditCardTypeBinNumberId || '-'}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid
                                                                            size={{
                                                                                xs: 3,
                                                                            }}
                                                                        >
                                                                            <Typography variant="body2">
                                                                                {item.creditCardTypeId || '-'}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid
                                                                            size={{
                                                                                xs: 4,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    color: '#2e7d32',
                                                                                    fontWeight: 500,
                                                                                }}
                                                                            >
                                                                                {item.binNumber || '-'}
                                                                            </Typography>
                                                                        </Grid>
                                                                        <Grid
                                                                            sx={{ textAlign: 'center' }}
                                                                            size={{
                                                                                xs: 2,
                                                                            }}
                                                                        >
                                                                            <Tooltip title="Sil" arrow>
                                                                                <IconButton
                                                                                    onClick={() =>
                                                                                        handleDeleteCreditCard(item)
                                                                                    }
                                                                                    sx={{
                                                                                        color: '#d32f2f',
                                                                                        '&:hover': {
                                                                                            backgroundColor: '#ffebee',
                                                                                            color: '#c62828',
                                                                                        },
                                                                                        transition: 'all 0.2s ease',
                                                                                    }}
                                                                                    size="small"
                                                                                >
                                                                                    <DeleteIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </Grid>
                                                                    </Grid>
                                                                </Box>
                                                            ))}
                                                        </Box>

                                                        {/* Pagination Info */}
                                                        <Box
                                                            sx={{
                                                                padding: '16px',
                                                                backgroundColor: '#f8f9fa',
                                                                borderTop: '1px solid #e0e0e0',
                                                                borderRadius: '0 0 8px 8px',
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <Typography variant="body2" color="text.secondary">
                                                                Toplam {creditCardList.length} kayıt gösteriliyor
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Paper>
                                    )}
                                </Box>
                            </Fade>
                        </Collapse>

                        {/* Help Text */}
                        {selectedCardType === null && (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    marginTop: '40px',
                                    padding: '20px',
                                    backgroundColor: '#fafafa',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0',
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    💡 Yukarıdaki butonlardan birini seçerek filtreleme alanlarına erişebilirsiniz
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
            {/* Debit Kart Yeni Bin Ekle Modal */}
            <Dialog
                className="bin-number-management-add-debit-bin-dialog"
                open={isAddDebitBinModalOpen}
                onClose={() => setIsAddDebitBinModalOpen(false)}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '16px',
                            boxShadow: '0 12px 32px rgba(25, 118, 210, 0.2)',
                        },
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        backgroundColor: '#1976d2',
                        color: 'white',
                        borderRadius: '16px 16px 0 0',
                        padding: '20px 24px',
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        🔵 {t('binNumberManagement.modals.debitCardTitle')}
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ padding: '32px 24px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {/* Seçili Banka Bilgisi */}
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 600,
                                    marginBottom: '12px',
                                    color: '#1976d2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                🏦 {t('binNumberManagement.modals.selectedBank')}
                            </Typography>
                            <Box
                                sx={{
                                    padding: '16px',
                                    backgroundColor: '#e3f2fd',
                                    borderRadius: '12px',
                                    border: '2px solid #bbdefb',
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#1976d2',
                                    }}
                                >
                                    {(() => {
                                        const selectedBank = bankList.find(
                                            (bank) =>
                                                bank.bankId.toString() === selectedBankId ||
                                                bank.bankId === parseInt(selectedBankId)
                                        );

                                        return (
                                            selectedBank?.bankName || t('binNumberManagement.modals.bankNotSelected')
                                        );
                                    })()}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Bin Numbers Input */}
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 600,
                                    marginBottom: '12px',
                                    color: '#1976d2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                🔢 {t('binNumberManagement.modals.binNumbersLabel')}
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder={t('binNumberManagement.modals.binNumbersPlaceholder')}
                                variant="outlined"
                                value={debitBinNumbers}
                                onChange={(e) => setDebitBinNumbers(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: '#f8f9fa',
                                        '& fieldset': {
                                            borderColor: '#bbdefb',
                                            borderWidth: '2px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#1976d2',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#1976d2',
                                        },
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        padding: '16px 24px 24px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '0 0 16px 16px',
                        gap: '12px',
                    }}
                >
                    <SCUIButton
                        className="bin-number-management-add-debit-cancel-button"
                        text={t('binNumberManagement.buttons.cancel')}
                        type="secondary"
                        sizeType="fixed"
                        size="m"
                        onClick={() => setIsAddDebitBinModalOpen(false)}
                        style={{
                            borderRadius: '8px',
                        }}
                    />
                    <SCUIButton
                        className="bin-number-management-add-debit-save-button"
                        text={
                            isDebitSaving
                                ? '⏳ ' + t('binNumberManagement.messages.saving')
                                : '✅ ' + t('binNumberManagement.buttons.save')
                        }
                        type="primary"
                        sizeType="fixed"
                        size="m"
                        onClick={handleDebitBinSave}
                        disabled={isDebitSaving}
                        style={{
                            backgroundColor: '#1976d2',
                            borderRadius: '8px',
                        }}
                    />
                </DialogActions>
            </Dialog>
            {/* Credit Kart Yeni Bin Ekle Modal */}
            <Dialog
                className="bin-number-management-add-credit-bin-dialog"
                open={isAddCreditBinModalOpen}
                onClose={() => setIsAddCreditBinModalOpen(false)}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '16px',
                            boxShadow: '0 12px 32px rgba(46, 125, 50, 0.2)',
                        },
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        backgroundColor: '#2e7d32',
                        color: 'white',
                        borderRadius: '16px 16px 0 0',
                        padding: '20px 24px',
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                        💳 {t('binNumberManagement.modals.creditCardTitle')}
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ padding: '32px 24px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {/* Seçili Credit Card Type Bilgisi */}
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 600,
                                    marginBottom: '12px',
                                    color: '#2e7d32',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                💳 {t('binNumberManagement.modals.selectedCreditCardType')}
                            </Typography>
                            <Box
                                sx={{
                                    padding: '16px',
                                    backgroundColor: '#e8f5e8',
                                    borderRadius: '12px',
                                    border: '2px solid #c8e6c9',
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#2e7d32',
                                    }}
                                >
                                    {(() => {
                                        const selectedType = creditCardTypeList.find(
                                            (type) =>
                                                type.creditCardTypeId.toString() === selectedCreditCardTypeId ||
                                                type.creditCardTypeId === parseInt(selectedCreditCardTypeId)
                                        );
                                        return (
                                            selectedType?.creditCardTypeName ||
                                            t('binNumberManagement.modals.creditCardTypeNotSelected')
                                        );
                                    })()}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Bin Numbers Input */}
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 600,
                                    marginBottom: '12px',
                                    color: '#2e7d32',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}
                            >
                                🔢 {t('binNumberManagement.modals.binNumbersLabel')}
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder={t('binNumberManagement.modals.binNumbersPlaceholder')}
                                variant="outlined"
                                value={creditBinNumbers}
                                onChange={(e) => setCreditBinNumbers(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        backgroundColor: '#f8f9fa',
                                        '& fieldset': {
                                            borderColor: '#c8e6c9',
                                            borderWidth: '2px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#2e7d32',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#2e7d32',
                                        },
                                    },
                                }}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    marginTop: '8px',
                                    display: 'block',
                                    fontStyle: 'italic',
                                }}
                            >
                                💡 {t('binNumberManagement.modals.binNumbersPlaceholder')}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        padding: '16px 24px 24px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '0 0 16px 16px',
                        gap: '12px',
                    }}
                >
                    <SCUIButton
                        className="bin-number-management-add-credit-cancel-button"
                        text={'❌ ' + t('binNumberManagement.buttons.cancel')}
                        type="secondary"
                        sizeType="fixed"
                        size="m"
                        onClick={() => setIsAddCreditBinModalOpen(false)}
                        style={{
                            borderRadius: '8px',
                        }}
                    />
                    <SCUIButton
                        className="bin-number-management-add-credit-save-button"
                        text={
                            isCreditSaving
                                ? '⏳ ' + t('binNumberManagement.messages.saving')
                                : '✅ ' + t('binNumberManagement.buttons.save')
                        }
                        type="primary"
                        sizeType="fixed"
                        size="m"
                        onClick={handleCreditBinSave}
                        disabled={isCreditSaving}
                        style={{
                            backgroundColor: '#2e7d32',
                            borderRadius: '8px',
                        }}
                    />
                </DialogActions>
            </Dialog>
            {/* Excel'den Toplu Bin Ekleme Modalı */}
            <Dialog
                className="bin-number-management-bulk-excel-debit-dialog"
                open={isBulkExcelModalOpen}
                onClose={() => setIsBulkExcelModalOpen(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '16px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        },
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        backgroundColor: '#1976d2',
                        color: 'white',
                        padding: '20px 24px',
                        fontSize: '20px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        borderRadius: '16px 16px 0 0',
                    }}
                >
                    📄 Excel&apos;den Toplu Bin Ekle
                </DialogTitle>

                <DialogContent sx={{ padding: '24px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {/* Açıklama */}
                        <Box
                            sx={{
                                backgroundColor: '#e3f2fd',
                                padding: '16px',
                                borderRadius: '12px',
                                border: '2px solid #bbdefb',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#1976d2',
                                    fontWeight: 500,
                                    marginBottom: '12px',
                                }}
                            >
                                📋 Excel Dosyası Formatı:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#424242', marginBottom: '8px' }}>
                                • <strong>A Kolonu:</strong> binNumbers (Bin numaraları)
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#424242', marginBottom: '8px' }}>
                                • <strong>B Kolonu:</strong> bankId (Banka ID&apos;si)
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#666',
                                    fontStyle: 'italic',
                                    display: 'block',
                                    marginTop: '12px',
                                }}
                            >
                                💡 İlk satır başlık satırıdır, veriler 2. satırdan başlamalıdır.
                            </Typography>
                        </Box>

                        {/* Dosya Seçme */}
                        <Box>
                            <Button
                                variant="contained"
                                component="label"
                                fullWidth
                                sx={{
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#1565c0',
                                    },
                                }}
                            >
                                📁 Excel Dosyası Seç
                                <input type="file" hidden accept=".xlsx,.xls" onChange={handleExcelFileUpload} />
                            </Button>
                            {selectedExcelFile && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        marginTop: '12px',
                                        color: '#2e7d32',
                                        fontWeight: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    ✅ Seçilen Dosya: {selectedExcelFile.name}
                                </Typography>
                            )}
                        </Box>

                        {/* Loading durumu */}
                        {isExcelProcessing && (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    padding: '20px',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '12px',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#1976d2',
                                        fontWeight: 500,
                                    }}
                                >
                                    ⏳ Excel dosyası işleniyor...
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        padding: '16px 24px 24px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '0 0 16px 16px',
                        gap: '12px',
                    }}
                >
                    <SCUIButton
                        className="bin-number-management-bulk-excel-debit-close-button"
                        text={'❌ Kapat'}
                        type="secondary"
                        sizeType="fixed"
                        size="m"
                        onClick={() => {
                            setIsBulkExcelModalOpen(false);
                            setSelectedExcelFile(null);
                        }}
                        style={{
                            borderRadius: '8px',
                        }}
                    />
                </DialogActions>
            </Dialog>
            {/* Credit Card - Excel'den Toplu Bin Ekleme Modalı */}
            <Dialog
                className="bin-number-management-bulk-excel-credit-dialog"
                open={isBulkCreditExcelModalOpen}
                onClose={() => setIsBulkCreditExcelModalOpen(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '16px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        },
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        backgroundColor: '#2e7d32',
                        color: 'white',
                        padding: '20px 24px',
                        fontSize: '20px',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        borderRadius: '16px 16px 0 0',
                    }}
                >
                    📄 Excel&apos;den Toplu Bin Ekle (Kredi Kartı)
                </DialogTitle>

                <DialogContent sx={{ padding: '24px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {/* Açıklama */}
                        <Box
                            sx={{
                                backgroundColor: '#e8f5e8',
                                padding: '16px',
                                borderRadius: '12px',
                                border: '2px solid #c8e6c9',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#2e7d32',
                                    fontWeight: 500,
                                    marginBottom: '12px',
                                }}
                            >
                                📋 Excel Dosyası Formatı:
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#424242', marginBottom: '8px' }}>
                                • <strong>A Kolonu:</strong> binNumbers (Bin numaraları)
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#424242', marginBottom: '8px' }}>
                                • <strong>B Kolonu:</strong> creditCardTypeId (Kredi Kartı Tip ID&apos;si)
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#666',
                                    fontStyle: 'italic',
                                    display: 'block',
                                    marginTop: '12px',
                                }}
                            >
                                💡 İlk satır başlık satırıdır, veriler 2. satırdan başlamalıdır.
                            </Typography>
                        </Box>

                        {/* Dosya Seçme */}
                        <Box>
                            <Button
                                variant="contained"
                                component="label"
                                fullWidth
                                sx={{
                                    backgroundColor: '#2e7d32',
                                    color: 'white',
                                    padding: '12px',
                                    borderRadius: '12px',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: '#1b5e20',
                                    },
                                }}
                            >
                                📁 Excel Dosyası Seç
                                <input type="file" hidden accept=".xlsx,.xls" onChange={handleCreditExcelFileUpload} />
                            </Button>
                            {selectedCreditExcelFile && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        marginTop: '12px',
                                        color: '#2e7d32',
                                        fontWeight: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    ✅ Seçilen Dosya: {selectedCreditExcelFile.name}
                                </Typography>
                            )}
                        </Box>

                        {/* Loading durumu */}
                        {isCreditExcelProcessing && (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    padding: '20px',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '12px',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#2e7d32',
                                        fontWeight: 500,
                                    }}
                                >
                                    ⏳ Excel dosyası işleniyor...
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        padding: '16px 24px 24px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '0 0 16px 16px',
                        gap: '12px',
                    }}
                >
                    <SCUIButton
                        className="bin-number-management-bulk-excel-credit-close-button"
                        text={'❌ Kapat'}
                        type="secondary"
                        sizeType="fixed"
                        size="m"
                        onClick={() => {
                            setIsBulkCreditExcelModalOpen(false);
                            setSelectedCreditExcelFile(null);
                        }}
                        style={{
                            borderRadius: '8px',
                        }}
                    />
                </DialogActions>
            </Dialog>
        </SCUIContent>
    );
}

export { BinNumberManagement };
